import { axiosFetch as authFetch } from './axiosFetch';
import { parseResponse } from './crypto';
import { ApiError } from '../types/api';
import { createRequest, createSignedHeaders, getUserId } from '../utils/hmac-utils';

const BASE_URL = process.env.REACT_APP_API_ENDPOINT || '';

function buildAuthHeaders(token?: string | null): Record<string, string> {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-source': 'MP',
    };
    if (token && typeof token === 'string' && token.trim()) {
        headers['Authorization'] = `Bearer ${token.trim()}`;
    }
    return headers;
}

async function signedGetRequest<T>(
    url: string,
    hmacData: Record<string, any>,
    token: string | null | undefined,
    parse: (response: Response) => Promise<T>,
    fallbackMessage: string,
): Promise<T> {
    const headers = await createSignedHeaders(hmacData, buildAuthHeaders(token));
    const response = await authFetch(url, { headers });
    if (!response.ok) {
        const errText = await response.text().catch(() => '');
        let errMsg = fallbackMessage;
        try { errMsg = JSON.parse(errText)?.message || errMsg; } catch { /* ignore */ }
        throw new Error(errMsg);
    }
    return parse(response);
}

async function signedJsonRequest(
    url: string,
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    body: Record<string, any>,
    token: string | null | undefined,
) {
    const headers = buildAuthHeaders(token);
    const userId = getUserId();
    const bodyWithUser = userId ? { userId, ...body } : body;
    const requestPayload = await createRequest(bodyWithUser);
    return authFetch(url, {
        method,
        headers,
        body: JSON.stringify(requestPayload),
    });
}

async function parseEmployeeResponse(response: Response): Promise<any> {
    const text = await response.text().catch(() => '');
    return parseResponse(text);
}

function employeeDataFromResponse(parsed: any) {
    return parsed?.metaDataInfo?.data
        ?? parsed?.data?.data
        ?? parsed?.data
        ?? parsed?.content
        ?? parsed;
}

async function employeeError(response: Response, fallbackMessage: string): Promise<ApiError> {
    const parsed = await parseEmployeeResponse(response);
    const message =
        parsed?.message
        ?? parsed?.metaDataInfo?.responseMessage
        ?? parsed?.error
        ?? fallbackMessage;
    return new ApiError(message, response.status);
}

export const loginApi = async ({ businessName, userId, password }: { businessName: string; userId: string; password: string }) => {
    const response = await fetch(`${BASE_URL}/merchants/staff/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ businessName, userId, password }),
    });
    const text = await response.text();
    const raw = parseResponse(text);
    const meta = raw?.metaDataInfo;
    if (meta?.responseCode === 'ERROR') {
        throw new Error(meta.responseMessage || 'Your User ID or Password is Incorrect');
    }
    if (!response.ok) {
        throw new Error(raw?.message || meta?.responseMessage || 'Your User ID or Password is Incorrect');
    }
    // CREDENTIALS = only the inner data object (id, merchantId, accessToken, ...). Never store metaDataInfo, content, or wrapper.
    const CREDENTIAL_KEYS = [
        'id', 'merchantId', 'locationId', 'businessName', 'email', 'userId', 'fullName', 'password', 'mobileNumber',
        'authUserId', 'devicePin', 'accessToken', 'refreshToken', 'deviceIdentifier', 'deviceType', 'role', 'blockUser',
        'attributes', 'defaultDeviceId', 'isSuperAdminAccess', 'favoriteTables', 'topicToSubscribe', 'firstName', 'lastName',
        'nickName', 'address', 'education', 'dateOfBirth', 'outlet', 'userAccessInfoList', 'userAccess', 'acceptCashPayment',
        'defaultFunctionalityAccessUpdated', 'toUseNickName', 'isTempPassword', 'myTableViewSectionExists',
        'location_id', 'access_token', 'refresh_token', 'staff_id',
    ];
    const from = raw?.metaDataInfo?.data ?? raw?.data ?? raw?.credentials ?? (Array.isArray(raw?.content) ? raw.content[0] : raw?.content) ?? raw;
    if (!from || typeof from !== 'object') return raw;
    const credentials: Record<string, any> = {};
    for (const key of CREDENTIAL_KEYS) {
        if (Object.prototype.hasOwnProperty.call(from, key)) credentials[key] = from[key];
    }
    if (from.access_token && !credentials.accessToken) credentials.accessToken = from.access_token;
    if (from.refresh_token && !credentials.refreshToken) credentials.refreshToken = from.refresh_token;
    if (from.location_id && !credentials.locationId) credentials.locationId = from.location_id;
    return Object.keys(credentials).length ? credentials : raw;
};

// Match maghil-portal: get staff's locations (branch list for switch)
export const getStaffLocations = async (staffId: string, token: string | null) => {
    return signedGetRequest(
        `${BASE_URL}/staff/${staffId}/locations`,
        { staffId },
        token,
        async (response) => parseResponse(await response.text()),
        'Failed to fetch locations',
    );
};

// Match maghil-portal: get location/branch details (branch list, logo) for branch switch
export const getLocationDetails = async (locationId: string, token: string | null) => {
    return signedGetRequest(
        `${BASE_URL}/merchants/location/${locationId}/details`,
        { locationId },
        token,
        async (response) => parseResponse(await response.text()),
        'Failed to fetch location details',
    );
};

export const getAuditLog = async ({ locationId, token, dateFrom, dateTo, page = 1, size = 20 }: { locationId: string; token?: string; dateFrom?: string; dateTo?: string; page?: number; size?: number }) => {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    return signedGetRequest(
        `${BASE_URL}/locations/${locationId}/audit-log?${params}`,
        { locationId, dateFrom, dateTo, page, size },
        token,
        async (response) => response.json(),
        'Failed to fetch audit log',
    );
};

export const getAuditLogEmployees = async ({ locationId, token }: { locationId: string; token?: string }) => {
    return signedGetRequest(
        `${BASE_URL}/locations/${locationId}/audit-log/employees`,
        { locationId },
        token,
        async (response) => response.json(),
        'Failed to fetch employees',
    );
};

// Employee Management APIs — matches maghil-portal employeeAPI.tsx
// POST /merchants/staffs
export const getEmployeeList = async ({ locationId, token }: { locationId: string; token?: string }) => {
    const response = await signedJsonRequest(`${BASE_URL}/merchants/staffs`, 'POST', { locationId }, token);
    if (!response.ok) {
        throw await employeeError(response, 'Failed to fetch employees');
    }
    const parsed = await parseEmployeeResponse(response);
    return employeeDataFromResponse(parsed) ?? [];
};

// POST /merchants/staffs/add — create new employee
export const createEmployeeApi = async ({ details, token, actionPerformedByStaffId }: { details: Record<string, any>; token?: string; actionPerformedByStaffId?: string }) => {
    const response = await signedJsonRequest(`${BASE_URL}/merchants/staffs/add`, 'POST', { ...details, actionPerformedByStaffId }, token);
    const text = await response.text();
    const parsed = parseResponse(text);
    const xmlSource = String(parsed?.dataString ?? parsed?.data ?? text ?? '');
    const xmlMsg = xmlSource.match(/<message>([\s\S]*?)<\/message>/i)?.[1]?.trim();
    const errorMessage =
        xmlMsg
        ?? parsed?.message
        ?? parsed?.data?.message
        ?? parsed?.metaDataInfo?.responseMessage
        ?? parsed?.metaDataInfo?.message
        ?? parsed?.error
        ?? 'Failed to create employee';
    const backendError = parsed?.metaDataInfo?.responseCode === 'ERROR' || parsed?.responseCode === 'ERROR';
    if (!response.ok || backendError) throw new Error(errorMessage);
    return parsed;
};

// GET /api-info â€” get roles and functions
export const getRolesAndFunctions = async ({ token }: { token?: string }) => {
    const { decryptJson } = await import('./crypto');
    return signedGetRequest(
        `${BASE_URL}/api-info`,
        { scope: 'roles-and-functions' },
        token,
        async (response) => {
            const json = await response.json();
            try { return decryptJson(json.data); } catch { return json; }
        },
        'Failed to fetch roles',
    );
};

// GET /merchants/{merchantId}/device-pin â€” get a new device PIN
export const getDevicePin = async ({ merchantId, token }: { merchantId: string; token?: string }) => {
    return signedGetRequest(
        `${BASE_URL}/merchants/${merchantId}/device-pin`,
        { merchantId },
        token,
        async (response) => response.json(),
        'Failed to get device PIN',
    );
};

// POST /merchants/staff/details — get single employee by staffId
export const getEmployeeById = async ({ staffId, merchantId, token }: { staffId: string; merchantId: string; token?: string }) => {
    const response = await signedJsonRequest(`${BASE_URL}/merchants/staff/details`, 'POST', { merchantId, staffId }, token);
    if (!response.ok) throw await employeeError(response, 'Failed to fetch employee details');
    const parsed = await parseEmployeeResponse(response);
    return employeeDataFromResponse(parsed) ?? null;
};

// PATCH /merchants/staffs/status — block/unblock employee
export const updateEmployeeStatus = async ({ staffId, isToBlock, token, actionPerformedByStaffId }: { staffId: string; isToBlock: boolean; token?: string; actionPerformedByStaffId?: string }) => {
    const response = await signedJsonRequest(`${BASE_URL}/merchants/staffs/status`, 'PATCH', { staffId, isToBlock, actionPerformedByStaffId }, token);
    const text = await response.text();
    if (!response.ok) throw new Error('Failed to update employee status');
    return parseResponse(text);
};

// PUT /merchants/staffs/update — edit/update employee
export const updateEmployeeApi = async ({ details, token, actionPerformedByStaffId }: { details: Record<string, any>; token?: string; actionPerformedByStaffId?: string }) => {
    const response = await signedJsonRequest(`${BASE_URL}/merchants/staffs/update`, 'PUT', { ...details, actionPerformedByStaffId }, token);
    const text = await response.text();
    const parsed = parseResponse(text);
    if (!response.ok) throw new Error(parsed?.message || 'Failed to update employee');
    return parsed;
};

// DELETE /merchants/staffs/remove — delete employee
export const deleteEmployeeApi = async ({ staffId, token, actionPerformedByStaffId }: { staffId: string; token?: string; actionPerformedByStaffId?: string }) => {
    const response = await authFetch(`${BASE_URL}/merchants/staffs/remove`, {
        method: 'DELETE',
        headers: buildAuthHeaders(token),
        body: JSON.stringify({ staffId, actionPerformedByStaffId }),
    });
    const text = await response.text();
    if (!response.ok) throw new Error('Failed to delete employee');
    return parseResponse(text);
};

// POST /merchants/staff/permissions â€” get employee roles/functions permissions
export const getStaffPermissions = async ({ staffId, merchantId, token }: { staffId: string; merchantId: string; token?: string }) => {
    const response = await signedJsonRequest(`${BASE_URL}/merchants/staff/permissions`, 'POST', { merchantId, staffId }, token);
    const text = await response.text();
    if (!response.ok) throw new Error('Failed to fetch staff permissions');
    return parseResponse(text);
};
